<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Orgs', to: '/settings/orgs' }
    ]"
  >
    <template #right>
      <Button
        label="New Org"
        severity="primary"
        icon="plus"
        @click="newOrg"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Orgs"
      :value="orgs"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-orgs"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.name }}</div>
        </template>
      </Column>

      <Column
        field="brandProfiles"
        header="Brand Profiles"
      >
        <template #body="{ data }">
          <div class="flex gap-2 flex-wrap">
            <Chip
              v-for="profile in data.brandProfiles"
              :key="profile.id"
            >
              <img
                v-if="profile.logoUrl"
                :src="profile.logoUrl"
                class="h-4 mr-2"
              />
              {{ profile.name }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="dealerships"
        header="Dealerships"
      >
        <template #body="{ data }">
          <div>{{ data.dealerships.length }}</div>
        </template>
      </Column>

      <Column
        field="regions"
        header="Regions"
      >
        <template #body="{ data }">
          <div class="flex gap-2">
            <Chip
              v-for="(region, index) in data.regions"
              :key="index"
            >
              <img
                :src="regionsList.find(r => r.value == region.toLowerCase())?.image"
                class="w-5 mr-2"
              />
              {{ region.toUpperCase() }}
            </Chip>
          </div>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Active"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isActive"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            Disabled
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              { value: true, label: 'Active' },
              { value: false, label: 'Disabled' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column :sortable="false">
        <template #body="{ data }">
          <div>
            <Button
              v-tippy="$t('ui.actions.edit')"
              icon="cog"
              plain
              severity="secondary"
              @click="editOrg(data.id)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="editOrgModalIsOpen"
      title="Edit Org"
      icon="briefcase-blank"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="editOrgHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="editOrgApiRequest.isLoading.value"
      :disable-close="editOrgApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((editOrgModalIsOpen = false), (editOrgHasUnsavedChanges = false))"
      @ok-modal="updateOrg()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          label="Active"
          class="col-span-2"
        >
          <ToggleSwitch v-model="selectedOrgState.isActive" />
        </FormGroup>
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="selectedOrgState.name"
            :has-error="v$.name.$error"
          />
          <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.locationSelection.region', 2)"
          :is-required="true"
        >
          <Select
            v-model="selectedOrgState.regions"
            :options="regionsList"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$.regions.$error">{{ v$.regions.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Default Brand Profile"
          class="col-span-2"
        >
          <Select
            v-model="selectedOrgState.brandProfileId"
            :options="selectedOrgProfileOptions"
            mode="single"
            :can-deselect="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          ></Select>
        </FormGroup>
      </div>

      <div class="mt-4">
        <div class="flex justify-between items-center mb-2">
          <div class="font-medium">Brand Profiles</div>
          <Button
            label="New Brand Profile"
            severity="secondary"
            outlined
            size="sm"
            icon="plus"
            @click="newBrandProfile"
          />
        </div>
        <div
          v-for="profile in selectedOrgState.brandProfiles"
          :key="profile.id"
          class="flex items-center justify-between border border-[var(--border-color)] rounded-md p-3 mb-2"
        >
          <div class="flex items-center gap-3">
            <div class="h-8 w-12 flex justify-center items-center">
              <Image
                v-if="profile.logoUrl"
                :src="profile.logoUrl"
                image-class="h-8 max-w-12"
              />
              <Icon
                v-else
                type="image-slash"
                class="text-secondary"
              />
            </div>
            <div>
              <div>{{ profile.name }}</div>
              <div class="flex items-center gap-2 text-sm text-secondary">
                <span
                  v-if="profile.accentColor"
                  class="inline-block h-3 w-3 rounded-full border border-[var(--border-color)]"
                  :style="{ backgroundColor: profile.accentColor }"
                ></span>
                {{ profile.accentColor ?? 'No accent colour' }}
              </div>
            </div>
          </div>
          <Button
            v-tippy="$t('ui.actions.edit')"
            icon="pen"
            plain
            severity="secondary"
            @click="editBrandProfile(profile)"
          ></Button>
        </div>
      </div>
    </Modal>

    <Modal
      :is-open="newOrgModalIsOpen"
      title="New Org"
      icon="briefcase-blank"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="newOrgHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Submit"
      :show-loader-on-confirm="true"
      :is-confirming="newOrgApiRequest.isLoading.value"
      :disable-close="newOrgApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((newOrgModalIsOpen = false), (newOrgHasUnsavedChanges = false))"
      @ok-modal="submitNewOrg()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="newOrgState.name"
            :has-error="v$new.name.$error"
          />
          <InputError :has-error="v$new.name.$error">{{ v$new.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.locationSelection.region')"
          :is-required="true"
        >
          <Select
            v-model="newOrgState.regions"
            :options="regionsList"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$new.regions.$error">{{ v$new.regions.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
      <div class="text-sm text-secondary">A default brand profile will be created for the org automatically.</div>
    </Modal>

    <Modal
      :is-open="brandProfileModalIsOpen"
      :title="brandProfileState.id ? 'Edit Brand Profile' : 'New Brand Profile'"
      icon="palette"
      :buttons="['ok', 'close']"
      close-text="Cancel"
      :ok-text="brandProfileState.id ? 'Save' : 'Create'"
      :show-loader-on-confirm="true"
      :is-confirming="brandProfileApiRequest.isLoading.value"
      :disable-close="brandProfileApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="brandProfileModalIsOpen = false"
      @ok-modal="submitBrandProfile()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="true"
        >
          <Input
            v-model="brandProfileState.name"
            :has-error="v$profile.name.$error"
          />
          <InputError :has-error="v$profile.name.$error">{{ v$profile.name.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup label="Accent Colour">
          <div class="flex items-center gap-2">
            <Input
              v-model="brandProfileState.accentColor"
              placeholder="#000000"
            />
            <span
              v-if="brandProfileState.accentColor"
              class="inline-block h-6 w-6 shrink-0 rounded-full border border-[var(--border-color)]"
              :style="{ backgroundColor: brandProfileState.accentColor }"
            ></span>
          </div>
        </FormGroup>
        <FormGroup label="UI Gradient 1">
          <Input
            v-model="brandProfileState.uiGradient1"
            placeholder="#000000"
          />
        </FormGroup>
        <FormGroup label="UI Gradient 2">
          <Input
            v-model="brandProfileState.uiGradient2"
            placeholder="#000000"
          />
        </FormGroup>
      </div>

      <div
        v-if="brandProfileState.id"
        class="mt-2"
      >
        <div class="font-medium mb-2">Logos</div>
        <div class="grid grid-cols-2 gap-x-6">
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (SVG)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoUrl"
                :src="brandProfileState.logoUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload SVG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('svg')"
            />
          </div>
          <div class="border border-[var(--border-color)] rounded-md p-3">
            <div class="text-sm text-secondary mb-2">Logo (PNG - used in emails)</div>
            <div class="h-12 flex items-center mb-2">
              <Image
                v-if="brandProfileState.logoPngUrl"
                :src="brandProfileState.logoPngUrl"
                image-class="h-10 max-w-24"
              />
              <span
                v-else
                class="text-sm text-secondary"
              >
                No logo uploaded
              </span>
            </div>
            <Button
              label="Upload PNG"
              severity="secondary"
              outlined
              size="sm"
              icon="cloud-arrow-up"
              :is-loading="logoIsUploading"
              @click="triggerLogoUpload('png')"
            />
          </div>
        </div>
        <input
          ref="logoFileInput"
          type="file"
          :accept="logoUploadType === 'svg' ? '.svg' : '.png'"
          style="display: none"
          @change="uploadLogo"
        />
      </div>
      <div
        v-else
        class="text-sm text-secondary mt-2"
      >
        Logos can be uploaded after the profile has been created.
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
import Icon from '@/components/icon/Icon.vue'
import Select from '@/components/forms/Select.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import { alertToast } from '@/utilities/notification'

import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'

const orgs = ref(null)
const orgsApiRequest = useApiRequest()
const editOrgApiRequest = useApiRequest()
const newOrgApiRequest = useApiRequest()
const brandProfileApiRequest = useApiRequest()
const logoUploadApiRequest = useApiRequest()
const isLoading = orgsApiRequest.isLoading

const editOrgModalIsOpen = ref(false)
const editOrgHasUnsavedChanges = ref(false)
const selectedOrgInitState = ref(null)
const selectedOrgState = ref(null)

const newOrgModalIsOpen = ref(false)
const newOrgHasUnsavedChanges = ref(false)
const newOrgInitState = ref(null)
const newOrgState = ref(null)

const brandProfileModalIsOpen = ref(false)
const brandProfileState = ref({})

const logoFileInput = ref(null)
const logoUploadType = ref('svg')
const logoIsUploading = logoUploadApiRequest.isLoading

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.IN },
    isActive: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-settings-orgs')
  initFilters()
}

const regionsList = [
  {
    label: useI18n().t('meta.countries.gb.label'),
    value: 'gb',
    emoji: '🇬🇧',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/GB.svg'
  },
  {
    label: useI18n().t('meta.countries.us.label'),
    value: 'us',
    emoji: '🇺🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg'
  },
  {
    label: useI18n().t('meta.countries.es.label'),
    value: 'es',
    emoji: '🇪🇸',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/ES.svg'
  },
  {
    label: useI18n().t('meta.countries.mx.label'),
    value: 'mx',
    emoji: '🇲🇽',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/MX.svg'
  },
  {
    label: useI18n().t('meta.countries.fr.label'),
    value: 'fr',
    emoji: '🇫🇷',
    image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/FR.svg'
  }
]

// Default profile picker only offers this org's own profiles
const selectedOrgProfileOptions = computed(() => {
  if (!selectedOrgState.value) {
    return []
  }
  return selectedOrgState.value.brandProfiles.map(profile => ({
    value: profile.id,
    label: profile.name
  }))
})

function fetchOrgs() {
  orgsApiRequest
    .send({
      endpoint: '/v1/orgs',
      params: { showDisabled: true }
    })
    .then(response => {
      if (response.success) {
        orgs.value = response.data
      } else {
        alertToast('Error', 'Failed to fetch orgs', 'error')
      }
    })
}
fetchOrgs()

function editOrg(id) {
  selectedOrgInitState.value = orgs.value.find(org => org.id == id)
  selectedOrgState.value = { ...selectedOrgInitState.value }
  if (selectedOrgState.value) {
    editOrgModalIsOpen.value = true
  } else {
    alertToast('Error', 'Could not find org', 'error')
  }
}

function newOrg() {
  newOrgInitState.value = {
    name: null,
    regions: null
  }

  newOrgState.value = { ...newOrgInitState.value }
  newOrgModalIsOpen.value = true
}

const orgInputRules = {
  name: { required },
  regions: { required }
}

const v$ = useVuelidate(orgInputRules, selectedOrgState, { $stopPropagation: true })
const v$new = useVuelidate(orgInputRules, newOrgState)

const brandProfileRules = {
  name: { required }
}
const v$profile = useVuelidate(brandProfileRules, brandProfileState, { $stopPropagation: true })

async function updateOrg() {
  const isFormCorrect = await v$.value.$validate()

  if (!isFormCorrect) {
    return
  }
  const dataToPost = {
    name: selectedOrgState.value.name,
    regions: selectedOrgState.value.regions,
    brandProfileId: selectedOrgState.value.brandProfileId,
    isActive: selectedOrgState.value.isActive
  }
  try {
    editOrgApiRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/orgs/${selectedOrgState.value.id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to update org', 'error')
        } else {
          alertToast('Success!', 'Org details updated', 'success')

          editOrgModalIsOpen.value = false
          editOrgHasUnsavedChanges.value = false
          fetchOrgs()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$.value.$reset()
}

async function submitNewOrg() {
  const isFormCorrect = await v$new.value.$validate()
  if (!isFormCorrect) {
    return
  }

  const dataToPost = { ...newOrgState.value }
  try {
    newOrgApiRequest
      .send({
        method: 'POST',
        endpoint: '/v1/orgs',
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to add org', 'error')
        } else {
          alertToast('Success!', 'Org added', 'success')
          newOrgModalIsOpen.value = false
          newOrgHasUnsavedChanges.value = false
          fetchOrgs()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$new.value.$reset()
}

function newBrandProfile() {
  brandProfileState.value = {
    id: null,
    orgId: selectedOrgState.value.id,
    name: null,
    logoUrl: null,
    logoPngUrl: null,
    accentColor: null,
    uiGradient1: null,
    uiGradient2: null
  }
  brandProfileModalIsOpen.value = true
}

function editBrandProfile(profile) {
  brandProfileState.value = { ...profile, orgId: selectedOrgState.value.id }
  brandProfileModalIsOpen.value = true
}

async function submitBrandProfile() {
  const isFormCorrect = await v$profile.value.$validate()
  if (!isFormCorrect) {
    return
  }

  const isNew = !brandProfileState.value.id
  const dataToPost = {
    name: brandProfileState.value.name,
    accentColor: brandProfileState.value.accentColor,
    uiGradient1: brandProfileState.value.uiGradient1,
    uiGradient2: brandProfileState.value.uiGradient2
  }
  // New profiles are owned by the currently open org
  if (isNew) {
    dataToPost.orgId = brandProfileState.value.orgId
  }

  try {
    const response = await brandProfileApiRequest.send({
      method: isNew ? 'POST' : 'PATCH',
      endpoint: isNew ? '/v1/brand-profiles' : `/v1/brand-profiles/${brandProfileState.value.id}`,
      data: JSON.stringify(dataToPost)
    })
    if (!response.success) {
      alertToast('Error', 'Failed to save brand profile', 'error')
      return
    }
    alertToast('Success!', 'Brand profile saved', 'success')

    if (isNew) {
      // Stay open in edit mode so logos can be uploaded straight away
      brandProfileState.value = { ...brandProfileState.value, ...response.data }
    } else {
      brandProfileModalIsOpen.value = false
    }
    await refreshSelectedOrg()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$profile.value.$reset()
}

// Re-fetch orgs and re-point the open edit modal at the fresh org record
async function refreshSelectedOrg() {
  const openOrgId = selectedOrgState.value?.id
  const response = await orgsApiRequest.send({ endpoint: '/v1/orgs', params: { showDisabled: true } })
  if (response.success) {
    orgs.value = response.data
    if (openOrgId) {
      const freshOrg = orgs.value.find(org => org.id === openOrgId)
      if (freshOrg) {
        // Keep any in-progress edits to the org fields, refresh the profile list
        selectedOrgState.value = { ...selectedOrgState.value, brandProfiles: freshOrg.brandProfiles }
        selectedOrgInitState.value = { ...selectedOrgInitState.value, brandProfiles: freshOrg.brandProfiles }
      }
    }
  }
}

function triggerLogoUpload(type) {
  logoUploadType.value = type
  logoFileInput.value.click()
}

async function uploadLogo(event) {
  const file = event.target.files[0]
  event.target.value = ''
  // Nothing selected in the file dialog
  if (!file) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await logoUploadApiRequest.send({
      method: 'POST',
      endpoint: `/v1/brand-profiles/${brandProfileState.value.id}/logo`,
      data: formData
    })
    if (!response.success) {
      alertToast('Error', 'Failed to upload logo', 'error')
      return
    }
    alertToast('Success!', 'Logo uploaded', 'success')
    // API returns logoUrl (svg) or logoPngUrl (png) for the uploaded file
    if (response.data.logoUrl) {
      brandProfileState.value.logoUrl = response.data.logoUrl
    }
    if (response.data.logoPngUrl) {
      brandProfileState.value.logoPngUrl = response.data.logoPngUrl
    }
    await refreshSelectedOrg()
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}

watch(
  selectedOrgState,
  newValue => {
    // Compare the current state with the initial state
    editOrgHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(selectedOrgInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)

watch(
  newOrgState,
  newValue => {
    // Compare the current state with the initial state
    newOrgHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(newOrgInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)
</script>

<style></style>
